import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import LogoMark from "./LogoMark";
import { useSmoothScroll } from "../context/ScrollContext";

const links = [
  { label: "Services", hash: "#services", testid: "nav-link-services" },
  { label: "Pricing", hash: "#pricing", testid: "nav-link-pricing" },
  { label: "About", hash: "#about", testid: "nav-link-about" },
  { label: "Service Area", hash: "#service-area", testid: "nav-link-service-area" },
  { label: "Contact", hash: "#contact", testid: "nav-link-contact" },
];

const Header = () => {
  const { scrollTo } = useSmoothScroll();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (hash) => {
    setOpen(false);
    scrollTo(hash);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-white/85 backdrop-blur-md border-b border-[#E6E0F0]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-[76px] flex items-center justify-between">
        <button
          data-testid="nav-logo-link"
          onClick={() => go("#top")}
          className="flex items-center gap-3 group text-left"
        >
          <LogoMark className="h-9 w-9 transition-transform duration-500 group-hover:-translate-y-0.5" />
          <span className="leading-none">
            <span className="block font-heading font-extrabold tracking-tight text-lg text-[#2B1B45]">
              Tri Valley
            </span>
            <span className="block text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5B5470] mt-0.5">
              Community Doula Services
            </span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <button
              key={l.hash}
              data-testid={l.testid}
              onClick={() => go(l.hash)}
              className="text-sm font-semibold text-[#5B5470] hover:text-[#F2644C] transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta-button"
            onClick={() => go("#contact")}
            className="text-sm font-bold px-5 py-2.5 rounded-full bg-[#F2644C] text-white hover:bg-[#D9523B] transition-all duration-300 shadow-sm hover:shadow-lg hover:-translate-y-0.5"
          >
            Book a consultation
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          className="lg:hidden p-2 text-[#2B1B45]"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-white/95 backdrop-blur-md border-b border-[#E6E0F0]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button
                  key={l.hash}
                  data-testid={`${l.testid}-mobile`}
                  onClick={() => go(l.hash)}
                  className="text-left text-base font-semibold text-[#2B1B45] hover:text-[#F2644C] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-cta-button-mobile"
                onClick={() => go("#contact")}
                className="mt-2 text-sm font-bold px-5 py-3 rounded-full bg-[#F2644C] text-white hover:bg-[#D9523B] transition-colors w-fit"
              >
                Book a consultation
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
